/**
 * R29 X4 — voice-orb state derivation (design-language §6, todo §10).
 *
 * The orb is the emotional centre of the Live page; on some screens it read static. This derives ONE
 * of four choreography states from state the client ALREADY has — NO new server wire, and crucially NO
 * per-audio-frame signal (the R18 orb-disturbance fix removed every per-frame read; re-introducing a
 * 15fps `botLevel` subscription would starve the audio worklet again). The inputs are all DISCRETE and
 * change at most a few times per turn:
 *
 *   - `status`        the session status string (store) — 'idle'|'connected'|… (changes per session)
 *   - `paused`        the visitor's manual hold (store) — a deliberate, infrequent toggle
 *   - `lastSpeaker`   speaker of the LAST transcript line, or null (store) — flips per turn
 *   - `lastFinal`     whether that last line is finalised (store) — flips per turn/chunk-settle
 *
 * States (design-language §6 choreography):
 *   idle       — no live session (breathing).
 *   speaking   — Gigi is mid-utterance: the last transcript line is HERS and not yet final (shimmer).
 *   listening  — connected, mic live, the floor is the visitor's: last line is the visitor's & not
 *                final, OR we're between turns waiting on them (ripple).
 *   thinking   — connected but the visitor has PAUSED the session (a held, gentle-spin state).
 *
 * A component subscribes to the DERIVED STRING via a zustand selector, so the orb re-renders only when
 * the state actually changes (≈once per turn), never per transcript chunk or audio frame.
 */

export type OrbState = 'idle' | 'listening' | 'speaking' | 'thinking';

export type OrbStateInputs = {
  /** Session status from the store ('idle' | 'connected' | 'warming' | … ). */
  status: string;
  /** Visitor's manual hold (store `paused`). */
  paused: boolean;
  /** Speaker of the last transcript line, or null when the transcript is empty. */
  lastSpeaker: 'visitor' | 'agent' | null;
  /** Whether that last transcript line has finalised. */
  lastFinal: boolean;
};

/**
 * Pure orb-state derivation. Order of precedence:
 *   1. not connected      → idle
 *   2. paused (held)      → thinking (a calm, deliberate hold)
 *   3. Gigi mid-utterance → speaking (agent line, not final)
 *   4. everything else    → listening (connected + the floor is the visitor's)
 */
export function deriveOrbState({
  status,
  paused,
  lastSpeaker,
  lastFinal,
}: OrbStateInputs): OrbState {
  if (status !== 'connected') return 'idle';
  if (paused) return 'thinking';
  if (lastSpeaker === 'agent' && !lastFinal) return 'speaking';
  return 'listening';
}
